"use client";

import Translations from "@translate/components/Chat/BusinessComponents/AssetTechnicalSignalSummaryBusinessChart";
import React, { useMemo } from "react";
import SignalLabelList from "@/src/components/DetailsCommon/Forecast/TechnicalSignals/SignalLabelList";
import SignalLevelIndicator from "@/src/components/DetailsCommon/Forecast/TechnicalSignals/SignalLevelIndicator";
import ForecastCard from "@/src/components/DetailsCommon/Forecast/ForecastCard";
import {
  useStockDetailsMarket,
  useStockDetailsHandle,
  useTokenDetailsMarket,
  useTokenDetailsHandle,
} from "@/src/hooks";
import { useTechnicalIndicators } from "@/src/hooks/useTechnicalIndicators";
import { FORECAST_QUERY_PARAMS } from "@config/stock";
import calculateTimeRange from "@/src/utils/calculateTimeRange";
import { I18nUtil } from "@utils/i18n";
import {
  ViewDetailsLink,
  buildStockDetailModuleHref,
  buildCryptoDetailHref,
} from "./ViewDetailsLink";

const I18n = I18nUtil.createScoped(Translations);

interface AssetTechnicalSignalSummaryBusinessChartProps {
  assetType: "stock" | "crypto";
  assetRouterId: string;
}

export const AssetTechnicalSignalSummaryBusinessChart: React.FC<
  AssetTechnicalSignalSummaryBusinessChartProps
> = ({ assetType, assetRouterId }) => {
  const isStock = assetType === "stock";

  const cryptoQueryParams = useMemo(() => {
    const { startTime, endTime } = calculateTimeRange("180d");
    return {
      "enrich.enable": true,
      "enrich.with_ohlc.enable": true,
      "enrich.with_ohlc.start_time": startTime,
      "enrich.with_ohlc.end_time": endTime,
      "enrich.with_ohlc.interval": "1d",
    };
  }, []);

  const { data: stockData, isLoading: stockLoading } = useStockDetailsMarket({
    enabled: isStock,
    queryParams: { ...FORECAST_QUERY_PARAMS, ids: [assetRouterId] },
    queryKey: ["chat-technical-summary", assetRouterId],
  });

  const { data: tokenList, isLoading: tokenLoading } = useTokenDetailsMarket({
    enabled: !isStock,
    ids: [assetRouterId],
    queryParams: cryptoQueryParams,
  });

  const isLoading = isStock ? stockLoading : tokenLoading;
  const stockSymbol = stockData?.symbol || "";

  const { ohlcv: stockOhlcv } = useStockDetailsHandle({
    tokenList: isStock ? stockData : undefined,
    symbol: stockSymbol,
  });

  const { ohlcv: tokenOhlcv, symbol: tokenSymbol } = useTokenDetailsHandle({
    tokenList: !isStock ? tokenList : undefined,
  });

  const ohlcv = isStock ? stockOhlcv : tokenOhlcv;
  const displaySymbol = isStock ? stockSymbol : tokenSymbol || assetRouterId;

  const { signalData, signalLevel } = useTechnicalIndicators({
    symbol: displaySymbol,
    isStock,
    ohlcv: ohlcv as any,
  });

  const labelList = useMemo(
    () => [
      {
        label: I18n.t("Buy Signals %{var0}", {
          var0: signalData?.buySignal || 0,
        }),
        className:
          "bg-background_accent1_overlay20 text-content_accent1_primary",
      },
      {
        label: I18n.t("Neutral Signals %{var0}", {
          var0: signalData?.neutralSignal || 0,
        }),
      },
      {
        label: I18n.t("Sell Signals %{var0}", {
          var0: signalData?.sellSignal || 0,
        }),
        className:
          "bg-background_accent2_overlay20 text-content_accent2_primary",
      },
    ],
    [signalData],
  );

  if (isLoading) {
    return (
      <div className="h-60 w-full animate-pulse bg-background_white_overlay5" />
    );
  }

  if (!signalData) {
    return null;
  }

  const detailHref = isStock
    ? buildStockDetailModuleHref(stockSymbol, "forecast")
    : buildCryptoDetailHref(assetRouterId);

  return (
    <>
      <div className="w-full overflow-hidden">
        <ForecastCard>
          <div className="w-full rounded-2xl gap-6 flex flex-col">
            <SignalLabelList labelList={labelList} />
            {signalLevel && <SignalLevelIndicator signalLevel={signalLevel} />}
          </div>
        </ForecastCard>
      </div>
      <ViewDetailsLink href={detailHref} />
    </>
  );
};
